import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { toast } from '@/providers/NotificationProvider'
import { applicationsAPI } from '@/services/api/applications'
import { useContextStore } from '@/store/context'
import { cn } from '@/utils/cn'

function slugifyApplicationKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

export default function CreateApplicationDialog({
  open,
  onOpenChange,
  organizationId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const deployments = useContextStore((state) => state.deployments)
  const applications = useContextStore((state) => state.applications)
  const setApplications = useContextStore((state) => state.setApplications)
  const setApplicationKey = useContextStore((state) => state.setApplicationKey)

  const [displayName, setDisplayName] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const [creating, setCreating] = useState(false)

  const sortedAgents = useMemo(
    () =>
      [...deployments].sort((a, b) =>
        (a.name || a.id).localeCompare(b.name || b.id, undefined, {
          sensitivity: 'base',
        }),
      ),
    [deployments],
  )

  useEffect(() => {
    if (!open) return
    setDisplayName('')
    setKey('')
    setKeyTouched(false)
    setSelectedAgentIds([])
    setCreating(false)
  }, [open])

  const toggleAgent = (agentId: string) => {
    setSelectedAgentIds((current) =>
      current.includes(agentId)
        ? current.filter((id) => id !== agentId)
        : [...current, agentId],
    )
  }

  const handleCreate = async () => {
    const trimmedKey = key.trim()
    if (!organizationId.trim()) {
      toast.error(t('contextSwitcher.createDeploymentNoOrganization'))
      return
    }
    if (!trimmedKey) {
      toast.error(t('contextSwitcher.createApplicationKeyRequired'))
      return
    }

    setCreating(true)
    try {
      const created = await applicationsAPI.create(organizationId, {
        key: trimmedKey,
        display_name: displayName.trim() || undefined,
        kind: 'application',
      })

      const memberErrors: string[] = []
      for (const agentId of selectedAgentIds) {
        try {
          await applicationsAPI.addAgentMember(
            organizationId,
            created.id,
            agentId,
          )
        } catch (memberError) {
          memberErrors.push(
            memberError instanceof Error
              ? memberError.message
              : String(memberError),
          )
        }
      }

      let detail = created
      try {
        detail = await applicationsAPI.get(organizationId, created.id)
      } catch {
        // keep create payload if reload fails
      }

      if (!applications.some((item) => item.id === detail.id)) {
        setApplications([...applications, detail])
      } else {
        setApplications(
          applications.map((item) =>
            item.id === detail.id ? { ...item, ...detail } : item,
          ),
        )
      }
      setApplicationKey(detail.key)
      localStorage.setItem('agent-os.current-application-key', detail.key)

      onOpenChange(false)
      navigate(`/studio/applications/${detail.id}`)
      toast.success(
        t('contextSwitcher.createApplicationSuccess', {
          name: detail.display_name || detail.key,
        }),
      )
      if (memberErrors.length > 0) {
        toast.error(
          t('contextSwitcher.createApplicationMembersPartial', {
            defaultValue:
              'Application créée, mais certains Agents n’ont pas pu être ajoutés.',
          }),
        )
      }
    } catch (createError) {
      const errorMessage =
        createError instanceof Error ? createError.message : ''
      toast.error(
        errorMessage || t('contextSwitcher.createApplicationFailed'),
      )
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!creating) onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('contextSwitcher.createApplicationTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('contextSwitcher.createApplicationDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="create-app-name">
              {t('contextSwitcher.createApplicationNameLabel')}
            </label>
            <Input
              id="create-app-name"
              value={displayName}
              onChange={(event) => {
                const nextName = event.target.value
                setDisplayName(nextName)
                if (!keyTouched) {
                  setKey(slugifyApplicationKey(nextName))
                }
              }}
              placeholder={t(
                'contextSwitcher.createApplicationNamePlaceholder',
              )}
              disabled={creating}
              data-testid="create-application-name-input"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="create-app-key">
              {t('contextSwitcher.createApplicationKeyLabel')}
            </label>
            <Input
              id="create-app-key"
              value={key}
              onChange={(event) => {
                setKeyTouched(true)
                setKey(slugifyApplicationKey(event.target.value))
              }}
              placeholder={t(
                'contextSwitcher.createApplicationKeyPlaceholder',
              )}
              disabled={creating}
              data-testid="create-application-key-input"
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">
              {t('contextSwitcher.createApplicationAgentsLabel', {
                defaultValue: 'Agents membres',
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('contextSwitcher.createApplicationAgentsHint', {
                defaultValue:
                  'Sélectionnez les Agents à inclure dans cette Application.',
              })}
            </p>
            {sortedAgents.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('contextSwitcher.createApplicationNoAgents', {
                  defaultValue: 'Aucun Agent disponible dans cette organisation.',
                })}
              </p>
            ) : (
              <div
                className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2"
                data-testid="create-application-agents"
              >
                {sortedAgents.map((agent) => {
                  const checked = selectedAgentIds.includes(agent.id)
                  return (
                    <label
                      key={agent.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60',
                        checked && 'bg-primary-soft',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAgent(agent.id)}
                        disabled={creating}
                        data-testid={`create-application-agent-${agent.id}`}
                      />
                      <span className="truncate">
                        {agent.name || agent.id}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating}
            data-testid="create-application-submit"
          >
            {creating
              ? t('contextSwitcher.createApplicationSubmitting', {
                  defaultValue: t('contextSwitcher.createDeploymentSubmitting'),
                })
              : t('contextSwitcher.createApplicationConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
